import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { createTestimonial, updateTestimonial, getTestimonials } from '../services/apiService';
import ImageUploader from '../components/ImageUploader';

const TestimonialForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    role: '',
    text: '',
    rating: 5,
    image: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit && id) {
      fetchTestimonial();
    }
  }, [isEdit, id]);

  const fetchTestimonial = async () => {
    try {
      const testimonials = await getTestimonials();
      const testimonial = testimonials.find(t => t.id === id);
      if (testimonial) {
        setFormData(testimonial);
      }
    } catch (error) {
      console.error('Error fetching testimonial:', error);
      setError('Erreur lors du chargement');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const testimonialData = {
        ...formData,
        rating: parseInt(formData.rating)
      };

      if (isEdit) {
        await updateTestimonial(id, testimonialData);
      } else {
        await createTestimonial(testimonialData);
      }

      navigate('/admin/dashboard?tab=testimonials');
    } catch (err) {
      console.error('Error saving:', err);
      setError('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/admin/dashboard?tab=testimonials')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Retour au dashboard
        </button>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <h1 className="text-3xl font-bold mb-8" style={{ color: '#5C3A21' }}>
            {isEdit ? 'Modifier le témoignage' : 'Ajouter un témoignage'}
          </h1>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Nom *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                  placeholder="Marie Dupont"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Entreprise</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                  placeholder="Atelier Créatif MD"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Poste</label>
                <input
                  type="text"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                  placeholder="Artisan créateur"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Note *</label>
                <select
                  name="rating"
                  value={formData.rating}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                >
                  <option value="5">5 étoiles</option>
                  <option value="4">4 étoiles</option>
                  <option value="3">3 étoiles</option>
                </select>
              </div>
            </div>

            <div>
              <ImageUploader
                label="Photo (optionnel)"
                value={formData.image}
                onChange={(v) => setFormData((prev) => ({ ...prev, image: v }))}
                hint="Portrait carré recommandé"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">Témoignage *</label>
              <textarea
                name="text"
                value={formData.text}
                onChange={handleChange}
                required
                rows="5"
                className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                placeholder="Votre témoignage ici..."
              />
            </div>

            <div className="flex items-center justify-end space-x-4 pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate('/admin/dashboard?tab=testimonials')}
                className="px-6 py-3 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 rounded-lg text-white font-semibold flex items-center space-x-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                style={{ backgroundColor: '#5C3A21' }}
              >
                <Save className="w-5 h-5" />
                <span>{loading ? 'Enregistrement...' : 'Enregistrer'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TestimonialForm;
